import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import logoigt from "../../assets/logoigt.png";
import "../../css/pages/login.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyPRLink, resetPassword, clearErrors } from "../../features/user/userSlice";

const SetNewPass = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");

  const { loading, error, linkValidated, resetToken, resetPasswordSuccess } =
    useSelector((state) => state.user);

  useEffect(() => {
    dispatch(clearErrors());
    if (id) {
      dispatch(verifyPRLink(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (resetPasswordSuccess) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [resetPasswordSuccess, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    setValidationError("");

    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

    if (!passwordRegex.test(newPassword)) {
      setValidationError(
        "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character.",
      );
      return;
    }

    dispatch(
      resetPassword({
        token: resetToken,
        password: newPassword,
      }),
    );
  };

  return (
    <Grid className="signin pt-10">
      <Grid
        item
        xs={8}
        md={4}
        component={Paper}
         className="auth-card"
        elevation={3}
        sx={{
          borderRadius: "4px",
        }}
      >
        <Box
          sx={{
            margin: "64px 32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                maxWidth: "200px",
                marginBottom: "20px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <img src={logoigt} alt="logo" style={{ width: "100%" }} />
            </Box>

            {/* Heading */}
            <Typography
              component="h1"
              variant="h5"
              sx={{
                color: "#585858",
                textAlign: "center",
                mb: 2,
              }}
            >
              Set New Password
            </Typography>

            {/* Verifying Link */}
            {loading && !linkValidated && !error && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  width: "100%",
                  maxWidth: "400px",
                }}
              >
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Verifying reset link...</Typography>
              </Box>
            )}

            {/* Valid Link */}
            {linkValidated && (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  label="New Password"
                  type="password"
                  value={newPassword}
                  disabled={loading}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <TextField
                  fullWidth
                  required
                  margin="normal"
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  disabled={loading}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "#666",
                  }}
                >
                  Password must contain:
                  <br />
                  • At least 8 characters
                  <br />
                  • One uppercase letter
                  <br />
                  • One lowercase letter
                  <br />
                  • One number
                  <br />• One special character
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || resetPasswordSuccess}
                  sx={{
                    mt: 2,
                    padding: "10px 16px",
                  }}
                >
                  {loading
                    ? "Updating Password..."
                    : resetPasswordSuccess
                      ? "Password Updated"
                      : "Update Password"}
                </Button>
              </form>
            )}

            {/* Validation Error */}
            {validationError && (
              <Typography
                color="error"
                sx={{
                  mt: 2,
                  textAlign: "center",
                  wordBreak: "break-word",
                }}
              >
                {validationError}
              </Typography>
            )}

            {/* Invalid / Expired Link */}
            {!linkValidated && error && (
              <Box
                sx={{
                  mt: 3,
                  textAlign: "center",
                }}
              >
                <Typography
                color="error"
                  sx={{
                    mb: 3,
                    whiteSpace: "pre-line",
                    wordBreak: "break-word",
                  }}
                >
                  {error}
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate("/forgot-password")}
                >
                  Request New Reset Link
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  sx={{ mt: 1 }}
                  onClick={() => navigate("/login")}
                >
                  Back to Sign In
                </Button>
              </Box>
            )}

            {/* Success Message */}
            {resetPasswordSuccess && (
              <Typography
                sx={{
                  color: "green",
                  mt: 2,
                  textAlign: "center",
                }}
              >
                {resetPasswordSuccess}
                <br />
                Redirecting to Sign In...
              </Typography>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SetNewPass;
